import { handleActions } from 'redux-actions';
import { replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import range from 'lodash/range';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import qs from 'qs';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

import createFilters from '../shared/filters';
import createCalendar from '../shared/calendar';
import moment from 'moment';

const calendar = createCalendar({
	namespace: 'postbackLog',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'postbackLog',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'offer_ids',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		}, {
			name: 'countries',
			title: 'Countries',
			placeholder: 'Add countries',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		}, {
			name: 'campaign_ids',
			title: 'Campaigns',
			placeholder: 'Add campaigns',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.CAMPAIGNS
			}
		}
	]
});

// actions types
const FETCH_DATA_SUCCESS = 'postback-log/fetch-data-success';
const FETCH_DATA_ERROR = 'postback-log/fetch-data-error';

const SORT_BY_COLUMN = 'postback-log/table-sort-by-column';
const GOTO_PAGE_SUCCESS = 'postback-log/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'postback-log/toggle-progress-bar';

const CLEAR = 'postback-log/clear';

const UPDATE_REFERER_TYPE_SUCCESS = 'postback-log/update-referer-type-success';
const UPDATE_REFERER_TYPE_ERROR = 'postback-log/update-referer-type-error';

const TOGGLE_AUTOARCHIVING = 'postback-log/toggle-autoarchiving';

// permissions
export const allowedPermissions = {
	view: 'campaigns_list'
};

// initial state
const initialState = {
	data: null,
	table: {
		columns: {
			date: {
				index: 'date',
				title: 'Date',
				direction: 'desc'
			},
			id: {
				index: 'affiliate_id',
				title: 'Affiliate ID',
				direction: 'desc'
			},
			url: {
				index: 'url',
				title: 'URL'
			},
			campaign_id: {
				index: 'campaign_id',
				title: 'Campaign ID/Conversion ID'
			},
			status: {
				index: 'status',
				title: 'Postback status'
			}
		},
		sortByColumn: {
			index: 'date',
			title: 'Date',
			direction: 'desc'
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	calendar: calendar.initialState,
	filters: filters.initialState,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { pagination: { perPage } } = getState().postbackLog;
			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'postbackLog');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`campaigns/?${qs.stringify(savedFilters)}`));
			}
			return request(0, perPage, getState, dispatch, externalQuery && externalQuery.query).then(action => {
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		search: () => (dispatch, getState) => {
			const { pagination: { perPage } } = getState().postbackLog;
			request(0, perPage, getState, dispatch);
		},

		apply: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			const { pagination: { perPage } } = getState().postbackLog;
			return request(0, perPage, getState, dispatch);
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('/campaigns'));
			dispatch({
				type: CLEAR
			});
		},

		sortByColumn: columnIndex => (dispatch, getState) => {
			dispatch({ type: SORT_BY_COLUMN, payload: columnIndex });
			dispatch(actions.fetchData({}));
		},

		gotoPage: page => (dispatch, getState) => {
			const { perPage } = getState().postbackLog.pagination;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			request(offset, perPage, getState, dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		toggleAutoArchiving: (campaign_ids, is_ignored_autoremove = true) => (dispatch, getState) => {
			return http({
				url: `${endpoints.CAMPAIGNS.BASE}/toggle-autoarchiving`,
				method: 'put',
				data: { campaign_ids: campaign_ids, is_ignored_autoremove },
				actions: [ TOGGLE_AUTOARCHIVING ]
			})(dispatch)
				.then(action => {
					if (action.type === TOGGLE_AUTOARCHIVING) {
						dispatch(actions.apply());
					}
				});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn['index'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;
			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[CLEAR]: state => ({ ...initialState }),
		[UPDATE_REFERER_TYPE_SUCCESS]: (state, action) => {
			return {
				...state,
				data: state.data.map(item => {
					if (item.id === action.meta.id) {
						return { ...item, referer_type: action.meta.type };
					}
					return item;
				})
			};
		},
		[UPDATE_REFERER_TYPE_ERROR]: state => ({ ...state, error: true })
	},
	calendar.reducers,
	filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.postbackLog.data,
	getTable: state => {
		const { table, data } = state.postbackLog;
		const rows = data || [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getSortByColumn: state => _get(state, 'postbackLog.table.sortByColumn', {}),
	getPagination: state => {
		const { perPage, total, currentPage } = state.postbackLog.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getFilters: state => filters.selectors.getFilters(state => state.postbackLog.filters, state => state)(state),
	getRefererTypeOptions: state => state.root.lists['referer-types'].map(i => ({ label: i.text, value: i.id })),
	getIntl: state => intl(state),
	getProgressBar: state => state.postbackLog.showProgressBar,
	getListedCampaignIds: state => {
		const table = selectors.getTable(state);

		return table.rows.map(row => row && row.id);
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getCalendar: state => calendar.selectors.getCalendar(state => state.postbackLog.calendar, state => state)(state),
	getError: state => state.postbackLog.error
};

// functions

const request = (offset = 0, limit = 50, getState, dispatch, externalQuery) => {
	const { filters: { selectedFilters } } = getState().postbackLog;
	const searchForm = getFormValues('campaignsSearchForm')(getState());
	const search = searchForm && searchForm.search || '';
	const filters = {};

	if (selectedFilters) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				filters[filter.name] = values;
			}
		});
	}
	const { start, end } = selectors.getCalendar(getState()).value;

	filters.date_range = {
		start: `${start}T00:00:00Z`,
		end: `${end}T23:59:59Z`
	};

	const query = qs.stringify({
		offset,
		limit,
		filters,
		sort: selectors.getSortByColumn(getState()),
		search,
		...externalQuery
	});

	return http({
		url: `${endpoints.POSTBACK_LOG.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/postback-log/index.js